import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { ROUTES, isInternalAccountRoute, isSingleSeatHiddenRoute, resolveStaffBounce } from 'routes'
import { useIsInternalUser } from 'hooks'
import { useVeraSpaceStore } from 'stores'
import { useCompanySeats } from 'hooks/api'

/**
 * Route guard for `/settings` — the mirror of `InternalRoute`.
 *
 * `InternalRoute` keeps brand users out of Vera's console; this keeps Vera staff out of
 * a brand's settings. An internal user reaching any settings URL outside the Account
 * allow-list (`isInternalAccountRoute`) is sent to their Account page instead.
 *
 * WHY A ROUTE GUARD AND NOT JUST A HIDDEN MENU (VRG-395 AC5): hiding nav is convenience,
 * not a boundary. Six company-settings pages are NOT rendered by `SettingsPage` at all —
 * `UserPage`, `RolePage`, `InvoicePage`, `BillingPlanChangePage`, `BillingPlanCheckoutPage`
 * and `IntegrationPage` are their own route elements — so a filter applied inside
 * `SettingsPage` would leave every one of them reachable by URL. Those are exactly the
 * URLs that get pasted into a thread and clicked later.
 *
 * ONE pathless wrapper around the whole settings block rather than a guard per route, so
 * the set it protects cannot drift: a settings route added later is inside the wrapper by
 * construction, and gets the gate without anyone remembering it exists. Enumerating the
 * ~22 company routes here would have been the same opt-in that §42 records 35 of 37 call
 * sites forgetting.
 *
 * This is a CONVENIENCE boundary, exactly as §27 says: the API's `@Permissions` guards
 * remain the real one. What it prevents is a staffer landing on a page whose every query
 * resolves against no company — which renders as an empty or, worse, an arbitrarily
 * scoped page rather than as an error.
 *
 * Auth is already settled here: every settings route sits under `ProtectedRoute`, which
 * holds a loader until `isLoading` clears, so there is no window where an internal user
 * is briefly seen as a company one and bounced the wrong way.
 *
 * VRG-227 — the same gate, for a SINGLE-SEAT company reaching Users / Roles &
 * permissions / Activity log by URL. Hiding the nav row (`SettingsPage`) is convenience
 * only; a bookmarked or pasted `/settings/users` link would otherwise still open. Uses
 * the SAME list (`isSingleSeatHiddenRoute`) `SettingsPage` filters its nav from, so the
 * two can never disagree about what exists.
 *
 * The seat query is disabled for internal users (no company to look up) and, while it is
 * loading, this gate does nothing — fail toward showing MORE (same stance
 * `SINGLE_SEAT_HIDDEN_ROUTES` documents), rather than bouncing a real team off Users for
 * the instant before seat data resolves.
 */
export const SettingsRoute = () => {
  const isInternal = useIsInternalUser()
  const { pathname } = useLocation()
  const { seats } = useCompanySeats({ enabled: !isInternal })
  // VRG-677: set only in the render right after "Return to Internal" (see `leaving` in
  // useVeraSpaceStore). Without it this bounce replaced the banner's navigation and the
  // staffer landed on /settings/profile.
  const leaving = useVeraSpaceStore((s) => s.leaving)

  if (isInternal && !isInternalAccountRoute(pathname)) {
    return <Navigate to={resolveStaffBounce(leaving, ROUTES.SETTINGS_PERSONAL_PROFILE)} replace />
  }

  if (
    !isInternal &&
    seats?.limit === 1 &&
    isSingleSeatHiddenRoute(pathname)
  ) {
    return <Navigate to={ROUTES.SETTINGS_COMPANY_GENERAL} replace />
  }

  return <Outlet />
}
